/**
 * Shared color palette for JavaScript/TypeScript code.
 *
 * These constants mirror the theme definitions in `tailwind.scss` and should be kept in sync.
 *
 * Purpose:
 *  - Provide a single source of truth for colors when working in JS/TS (e.g., Chart.js, Naive UI overrides).
 *  - Avoid hardcoding color values in multiple places, reducing the risk of typos and mismatched themes.
 */
export const COLORS = {
    neutral: {
        bg: '25 28 22',
        bgHover: '39 43 35',
        text: '255 255 255',
        textLight: '127 130 123',
        border: '98 102 95',
    },
    neutralLight: {
        bg: '255 255 255',
        bgHover: '246 248 243',
        text: '29 31 27',
        textLight: '82 84 79',
        border: '210 215 204',
    },
    green: {
        bg: '97 128 57',
        bgHover: '85 115 46',
        text: '255 255 255',
        textLight: '153 170 132',
        border: '129 151 101',
    },
    greenLight: {
        bg: '222 229 213',
        bgHover: '211 222 196',
        text: '24 36 26',
        textLight: '69 87 73',
        border: '174 185 159',
    },
    coral: {
        bg: '214 106 94',
        bgHover: '203 96 84',
        text: '255 255 255',
        textLight: '255 225 222',
        border: '244 166 157',
    },
    blue: {
        bg: '0 70 96',
        bgHover: '1 61 84',
        text: '255 255 255',
        textLight: '176 194 201',
        border: '64 102 117',
    },
    sky: {
        bg: '144 209 242',
        bgHover: '121 191 226',
        text: '21 38 47',
        textLight: '99 156 185',
        border: '90 144 172',
    },
    warning: {
        bg: '235 182 45',
        bgHover: '222 170 36',
        text: '255 255 255',
        textLight: '255 237 190',
        border: '255 237 190',
    },
    error: {
        bg: '211 71 56',
        bgHover: '184 48 33',
        text: '255 255 255',
        textLight: '255 176 168',
        border: '255 122 107',
    },
} as const;

export function toRGB(value: string): string {
    return `rgb(${value.trim().replace(/\s+/g, ', ')})`
}
